import React, { Fragment, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import Link from '@atlaskit/link';
import { Box, Grid, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { TabPanel } from '@atlaskit/tabs';
import { token } from '@atlaskit/tokens';

import { ErrorBoundary } from '../../../common/ui/error-boundary';
import { messages } from '../messages';
import { FunctionResult } from '../minions-output-items/function-result';
import { PluginOutputRenderer } from '../plugin-output-renderer';
import { MinionName, type MinionOutput, type OrchestrationRound } from '../types';
import { getMinionOutputItem, getRankedDocumentsWithChunks, parseMinionExtraInfo } from '../utils';

import { Panel } from './panel-wrapper';

const styles = cssMap({
	minionCard: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		overflow: 'hidden',
	},
	pressable: {
		paddingTop: token('space.150'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		cursor: 'pointer',
		width: '100%',
	},
	expandedContent: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('color.background.neutral'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	// Reusable bordered box style
	borderedBox: {
		backgroundColor: token('color.background.neutral.subtle'),
		borderRadius: token('radius.small'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border'),
		overflow: 'hidden',
	},
	sectionPadding: {
		paddingTop: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.200'),
		paddingRight: token('space.200'),
	},
	codeBlock: {
		backgroundColor: token('color.background.input'),
		borderRadius: token('radius.small'),
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-all',
		maxHeight: '200px',
		overflow: 'auto',
	},
	// Expandable section styles
	expandableHeader: {
		paddingTop: token('space.100'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		cursor: 'pointer',
		width: '100%',
	},
	expandableContent: {
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
		borderTopWidth: token('border.width'),
		borderTopStyle: 'solid',
		borderTopColor: token('color.border'),
	},
	// Table styles for search results
	searchResultsGrid: {
		gridTemplateColumns: '1fr',
	},
	rankedDocsGrid: {
		gridTemplateColumns: '50px 1fr 80px 80px 80px',
	},
	gridCell: {
		paddingTop: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.150'),
		paddingRight: token('space.150'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
		wordBreak: 'break-all',
	},
	gridCellWithLeftBorder: {
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderLeftColor: token('color.border'),
	},
	gridHeader: {
		backgroundColor: token('color.background.neutral'),
	},
});

// Expandable Section Component for Raw/Ranked Results
type ExpandableSectionProps = {
	title: string;
	children: React.ReactNode;
	defaultExpanded?: boolean;
};

const ExpandableSection = ({
	title,
	children,
	defaultExpanded = false,
}: ExpandableSectionProps) => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(defaultExpanded);

	return (
		<Box xcss={styles.borderedBox}>
			<Pressable onClick={() => setIsExpanded(!isExpanded)} xcss={styles.expandableHeader}>
				<Inline space="space.100" alignBlock="center">
					{isExpanded ? (
						<ChevronDownIcon
							label={formatMessage(messages.ai_debugger_modal_collapse_label)}
							color="currentColor"
						/>
					) : (
						<ChevronRightIcon
							label={formatMessage(messages.ai_debugger_modal_expand_label)}
							color="currentColor"
						/>
					)}
					<Text size="small">{title}</Text>
				</Inline>
			</Pressable>
			{isExpanded && <Box xcss={styles.expandableContent}>{children}</Box>}
		</Box>
	);
};

type MinionExpandableItemProps = {
	minion: MinionOutput;
	index: number;
};

const MinionExpandableItem = ({ minion, index }: MinionExpandableItemProps) => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const minionInfo = getMinionOutputItem(minion);
	const isSearchMinion = minionInfo.minion_id?.toLowerCase().includes(MinionName.SEARCH);

	const { extraInfo, parsedOutput, functionResults, searchExtraInfo } = parseMinionExtraInfo(
		minionInfo.minion_extra_info,
		minionInfo.minion_id,
		isSearchMinion ?? false,
	);

	const rankedDocumentsWithChunks = getRankedDocumentsWithChunks(searchExtraInfo);
	const rawSearchResults = searchExtraInfo?.searchResults ?? [];
	const executionDuration = minionInfo.minion_execution_duration;

	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Box xcss={styles.minionCard}>
				<Pressable onClick={() => setIsExpanded(!isExpanded)} xcss={styles.pressable}>
					<Inline space="space.100" alignBlock="center" spread="space-between">
						<Inline space="space.100" alignBlock="center">
							{isExpanded ? (
								<ChevronDownIcon
									label={formatMessage(messages.ai_debugger_modal_collapse_label)}
									color="currentColor"
								/>
							) : (
								<ChevronRightIcon
									label={formatMessage(messages.ai_debugger_modal_expand_label)}
									color="currentColor"
								/>
							)}
							<Text weight="medium">{minionInfo.minion_id || `Minion ${index + 1}`}</Text>
						</Inline>
						{executionDuration !== undefined && (
							<Text size="small" color="color.text.subtle">
								{formatMessage(messages.ai_debugger_modal_execution_duration_label)}:{' '}
								{formatMessage(messages.ai_debugger_modal_execution_duration, {
									duration: executionDuration,
								})}
							</Text>
						)}
					</Inline>
				</Pressable>

				{isExpanded && (
					<Box xcss={styles.expandedContent}>
						<Stack space="space.200">
							{/* Minion Input */}
							{minionInfo.minion_input && (
								<Box xcss={cx(styles.borderedBox, styles.sectionPadding)}>
									<Stack space="space.100">
										<Text weight="semibold" size="small">
											{formatMessage(messages.ai_debugger_modal_minion_input_label)}
										</Text>
										<Box xcss={styles.codeBlock}>
											<Text size="small">
												{typeof minionInfo.minion_input === 'object'
													? JSON.stringify(minionInfo.minion_input, null, 2)
													: minionInfo.minion_input}
											</Text>
										</Box>
									</Stack>
								</Box>
							)}

							{/* Minion Output - Expandable */}
							{minionInfo.minion_output && (
								<ExpandableSection
									title={formatMessage(messages.ai_debugger_modal_minion_output_label)}
								>
									<Box xcss={styles.codeBlock}>
										<Text size="small">
											{typeof minionInfo.minion_output === 'object'
												? JSON.stringify(minionInfo.minion_output, null, 2)
												: minionInfo.minion_output}
										</Text>
									</Box>
								</ExpandableSection>
							)}

							{/* Function Results (for agents like Teamwork) - Expandable */}
							{functionResults && functionResults.length > 0 && (
								<ExpandableSection
									title={formatMessage(messages.ai_debugger_modal_function_results_count, {
										count: functionResults.length,
									})}
								>
									<Stack space="space.100">
										{functionResults.map((result, idx) => (
											<FunctionResult key={idx} result={result} index={idx} />
										))}
									</Stack>
								</ExpandableSection>
							)}

							{/* Minion Extra Info (parsed structured data), fallthrough to extraInfo - Expandable */}
							{(parsedOutput || extraInfo) && (
								<ExpandableSection
									title={formatMessage(messages.ai_debugger_modal_minion_extra_info_label)}
								>
									<PluginOutputRenderer parsedOutput={parsedOutput} extraInfo={extraInfo} />
								</ExpandableSection>
							)}

							{/* Search Minion: Raw Search Results - Expandable */}
							{isSearchMinion && rawSearchResults.length > 0 && (
								<ExpandableSection
									title={formatMessage(messages.ai_debugger_modal_raw_search_results_heading, {
										count: rawSearchResults.length,
									})}
								>
									<Box xcss={styles.borderedBox}>
										<Grid xcss={styles.searchResultsGrid}>
											<Box xcss={cx(styles.gridCell, styles.gridHeader)}>
												<Text weight="semibold">
													{formatMessage(messages.ai_debugger_modal_table_url_header)}
												</Text>
											</Box>
											{rawSearchResults.map((url) => (
												<Box key={url} xcss={styles.gridCell}>
													<Link href={url} target="_blank">
														{url}
													</Link>
												</Box>
											))}
										</Grid>
									</Box>
								</ExpandableSection>
							)}

							{/* Search Minion: Ranked Search Results - Expandable */}
							{isSearchMinion && rankedDocumentsWithChunks.length > 0 && (
								<ExpandableSection
									title={formatMessage(messages.ai_debugger_modal_ranked_search_results_heading, {
										count: rankedDocumentsWithChunks.length,
									})}
								>
									<Box xcss={styles.borderedBox}>
										<Grid xcss={styles.rankedDocsGrid}>
											{/* Header row */}
											<Box xcss={cx(styles.gridCell, styles.gridHeader)}>
												<Text weight="semibold">
													{formatMessage(messages.ai_debugger_modal_table_doc_index_header)}
												</Text>
											</Box>
											<Box
												xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder, styles.gridHeader)}
											>
												<Text weight="semibold">
													{formatMessage(messages.ai_debugger_modal_table_url_header)}
												</Text>
											</Box>
											<Box
												xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder, styles.gridHeader)}
											>
												<Text weight="semibold">
													{formatMessage(messages.ai_debugger_modal_table_chunk_id_header)}
												</Text>
											</Box>
											<Box
												xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder, styles.gridHeader)}
											>
												<Text weight="semibold">
													{formatMessage(messages.ai_debugger_modal_table_chunk_score_header)}
												</Text>
											</Box>
											<Box
												xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder, styles.gridHeader)}
											>
												<Text weight="semibold">
													{formatMessage(messages.ai_debugger_modal_table_chunk_rank_header)}
												</Text>
											</Box>

											{/* Data rows */}
											{rankedDocumentsWithChunks.map((doc, docIndex) => {
												const chunkCount = Math.max(doc.chunks.length, 1);

												return (
													<Fragment key={doc.url}>
														<Box xcss={styles.gridCell} style={{ gridRow: `span ${chunkCount}` }}>
															<Text weight="semibold">{docIndex + 1}</Text>
														</Box>
														<Box
															xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder)}
															style={{ gridRow: `span ${chunkCount}` }}
														>
															<Link href={doc.url} target="_blank">
																{doc.url}
															</Link>
														</Box>

														{doc.chunks.length > 0 ? (
															doc.chunks.map((chunk) => (
																<Fragment key={chunk.chunk_id}>
																	<Box xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder)}>
																		<Text>{chunk.chunk_id}</Text>
																	</Box>
																	<Box xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder)}>
																		<Text>{chunk.score.toFixed(4)}</Text>
																	</Box>
																	<Box xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder)}>
																		<Text>{chunk.rank}</Text>
																	</Box>
																</Fragment>
															))
														) : (
															<Fragment>
																<Box xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder)}>
																	<Text color="color.text.subtlest">
																		{formatMessage(
																			messages.ai_debugger_modal_unable_to_match_chunks,
																		)}
																	</Text>
																</Box>
																<Box xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder)}>
																	<Text>-</Text>
																</Box>
																<Box xcss={cx(styles.gridCell, styles.gridCellWithLeftBorder)}>
																	<Text>-</Text>
																</Box>
															</Fragment>
														)}
													</Fragment>
												);
											})}
										</Grid>
									</Box>
								</ExpandableSection>
							)}
						</Stack>
					</Box>
				)}
			</Box>
		</ErrorBoundary>
	);
};

type LoopPanelProps = {
	round: OrchestrationRound;
	roundIndex: number;
};

export const LoopPanel: ({ round, roundIndex }: LoopPanelProps) => React.JSX.Element = ({
	round,
	roundIndex,
}: LoopPanelProps) => {
	const { formatMessage } = useIntl();

	return (
		<TabPanel>
			<Panel testId={`sain-online-debugger-loop-${roundIndex}-panel`}>
				<Stack space="space.150">
					<Heading size="xsmall">
						{formatMessage(messages.ai_debugger_modal_orchestration_loop_with_count, {
							number: roundIndex,
							count: round.length,
						})}
					</Heading>
					<Stack space="space.100">
						{round.map((minion, minionIndex) => (
							<MinionExpandableItem
								key={getMinionOutputItem(minion).minion_id || minionIndex}
								minion={minion}
								index={minionIndex}
							/>
						))}
					</Stack>
				</Stack>
			</Panel>
		</TabPanel>
	);
};
